import type { ScreeningView } from '@/modules/tools/bankruptcy/stores/types'
import { currencyFromCents, percentFromRate } from '@/utils'
import { AssetsTable } from './AssetsTable'
import { FindingsReport } from './FindingsReport'
import { IgnoredPagesReport } from './IgnoredPagesReport'
import { Row } from './Row'
import { Section } from './Section'
import { SourceViewer } from './SourceViewer'

export const ScreeningBreakdown = ({ filing }: { filing: ScreeningView }) => {
  const { breakdown, ignoredPages } = filing
  const equity = breakdown.nonExemptEquityCents ?? 0
  const distribution = breakdown.estimatedDistributionCents ?? 0
  const commission = breakdown.estimatedCommissionCents ?? 0
  const liquidationCost = equity - distribution
  const tiers = breakdown.commissionTiers ?? []
  const findings = breakdown.findings ?? []
  const unverified = breakdown.unverified ?? false

  if (breakdown.chapter7 === false) {
    return (
      <div className="space-y-4">
        <Section
          title="How we got here"
          hint="Only a Chapter 7 case carries a liquidation distribution."
        >
          <p className="text-muted-foreground text-sm">
            This case is not a Chapter 7, so it ranks at zero.
          </p>
          <IgnoredPagesReport ignoredPages={ignoredPages} />
        </Section>

        <Section
          title="Findings"
          hint="Leads for the trustee; nothing is changed."
        >
          <FindingsReport findings={findings} />
        </Section>

        <SourceViewer />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Section
        title="Scheduled equity"
        hint="Each asset's value, less any lien and claimed exemption."
      >
        <AssetsTable assets={breakdown.assets ?? []} />
        <div className="flex justify-between border-t pt-3 font-medium">
          <span>Scheduled equity</span>
          <span className="tabular-nums">{currencyFromCents(equity)}</span>
        </div>
      </Section>

      <Section title="Est. proceeds" hint="After liquidation costs">
        <dl className="space-y-1.5 text-sm">
          <Row label="Scheduled equity" value={currencyFromCents(equity)} />
          <Row
            label={`Est. liquidation (${percentFromRate(breakdown.liquidationCostRate)})`}
            value={`−${currencyFromCents(liquidationCost)}`}
            muted
          />
          <Row
            label="Est. proceeds"
            value={currencyFromCents(distribution)}
            strong
          />
        </dl>
        {unverified && (
          <p className="text-amber-600 text-xs">
            Unverified — the packet's printed totals did not add up.
          </p>
        )}
      </Section>

      <Section
        title="§326 commission cap"
        hint="Based on estimated disbursements"
      >
        <dl className="space-y-1.5 text-sm">
          {tiers.map((tier, index) => (
            <Row
              key={`${tier.rate}-${index}`}
              label={`${percentFromRate(tier.rate)} of ${currencyFromCents(tier.bandCents)}`}
              value={currencyFromCents(tier.amountCents)}
              muted
            />
          ))}
          <Row
            label="Est. §326 cap"
            value={currencyFromCents(commission)}
            strong
          />
        </dl>
      </Section>

      <Section
        title="Findings"
        hint="Leads for the trustee; nothing is changed."
      >
        <FindingsReport findings={findings} />
      </Section>

      <Section
        title="Not scored"
        hint="Every page is accounted for — nothing dropped silently."
      >
        <IgnoredPagesReport ignoredPages={ignoredPages} />
      </Section>

      <SourceViewer />
    </div>
  )
}
